import { useMemo, useState } from 'react';
import type { Preventivo, EstimateLevel } from '../../types';
import { PreventivoCard } from './PreventivoCard';
import { formatCurrency } from '../../utils/pricing';

interface PreventiviListProps {
  preventivi: Preventivo[];
  estimateLevel: EstimateLevel;
  onEdit: (preventivo: Preventivo) => void;
  onDelete: (id: string) => void;
  selectedId?: string;
  onSelect?: (preventivo: Preventivo) => void;
}

export function PreventiviList({
  preventivi,
  estimateLevel,
  onEdit,
  onDelete,
  selectedId,
  onSelect,
}: PreventiviListProps) {
  const [filterCategoria, setFilterCategoria] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Get unique categories
  const categorie = useMemo(() => {
    const cats = new Set(preventivi.map((p) => p.categoria));
    return Array.from(cats).sort();
  }, [preventivi]);

  // Filter preventivi
  const filteredPreventivi = useMemo(() => {
    return preventivi.filter((p) => {
      const matchesCategoria = !filterCategoria || p.categoria === filterCategoria;
      const matchesSearch =
        !searchTerm ||
        p.voce.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.categoria.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategoria && matchesSearch;
    });
  }, [preventivi, filterCategoria, searchTerm]);

  // Group by category
  const groupedPreventivi = useMemo(() => {
    const groups: Record<string, Preventivo[]> = {};
    filteredPreventivi.forEach((p) => {
      if (!groups[p.categoria]) {
        groups[p.categoria] = [];
      }
      groups[p.categoria].push(p);
    });
    return groups;
  }, [filteredPreventivi]);

  // Calculate totals
  const totals = useMemo(() => {
    const key = `costo${estimateLevel.charAt(0).toUpperCase() + estimateLevel.slice(1)}` as
      | 'costoMin'
      | 'costoMed'
      | 'costoMax';
    return filteredPreventivi.reduce((sum, p) => sum + parseFloat(p[key]), 0);
  }, [filteredPreventivi, estimateLevel]);

  if (preventivi.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">
          Nessun preventivo
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Aggiungi il primo preventivo per iniziare a tracciare i costi.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Cerca preventivo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <select
          value={filterCategoria}
          onChange={(e) => setFilterCategoria(e.target.value)}
          className="sm:w-48"
        >
          <option value="">Tutte le categorie</option>
          {categorie.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Summary */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex justify-between items-center">
        <div>
          <span className="text-sm text-primary-700">
            {filteredPreventivi.length} preventiv
            {filteredPreventivi.length === 1 ? 'o' : 'i'}
            {filterCategoria && ` in "${filterCategoria}"`}
          </span>
        </div>
        <div className="text-right">
          <span className="text-sm text-primary-700">Totale: </span>
          <span className="font-bold text-primary-800 currency">
            {formatCurrency(totals)}
          </span>
        </div>
      </div>

      {/* Grouped List */}
      <div className="space-y-6">
        {Object.entries(groupedPreventivi).map(([categoria, items]) => (
          <div key={categoria}>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              {categoria}
              <span className="ml-2 text-xs font-normal text-gray-400">
                ({items.length})
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((preventivo) => (
                <PreventivoCard
                  key={preventivo.id}
                  preventivo={preventivo}
                  estimateLevel={estimateLevel}
                  onEdit={() => onEdit(preventivo)}
                  onDelete={() => onDelete(preventivo.id)}
                  isSelected={selectedId === preventivo.id}
                  onClick={() => onSelect?.(preventivo)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
